import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { followBannerLink } from '../utils/bannerLink';
import { Carousel, CarouselContent, CarouselItem } from './ui/carousel';
import { readCache, writeCache, CACHE_KEYS } from '../utils/menuCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * BannerSlot — single renderer for promotional banners. One <BannerSlot zone=".." />
 * lives per zone on the customer surfaces (3 on Landing, 1 on Offers).
 *
 * Fetches active+scheduled+branch-matched banners for the zone, renders nothing
 * when the list is empty (banners are non-essential UI; no skeleton flash).
 *
 * Carousel zones auto-rotate every 5s and pause on user touch/hover. Single-banner
 * zones render the one image directly.
 *
 * Impressions log once per (banner, page-load) via IntersectionObserver. Clicks
 * fire-and-forget the click log before routing. None of the analytics logs can
 * fail the navigation — they're best-effort.
 */
const CAROUSEL_ZONES = new Set(['home_hero_carousel', 'home_pre_categories', 'offers_top']);
const AUTOPLAY_MS = 5000;

// Pixel widths the server produces for each banner — keep in sync with
// `BANNER_IMAGE_WIDTHS` in `backend/routers/banners.py`. The `sizes` hint
// tells the browser the image fills the viewport on phones/tablets and sits
// in Landing's 48rem desktop column (so 768 px, not the window width), so it
// picks the right srcset entry.
const BANNER_SRCSET_WIDTHS = { small: 480, medium: 960, large: 1600 };
const BANNER_SIZES_HINT = '(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 768px';

const resolveImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

/**
 * Build the `srcset` string for a banner.image_urls map. Returns an empty
 * string when the legacy single-URL shape is in play — the consuming <img>
 * then falls back to a plain `src` and the browser scales it client-side
 * (works, just bandwidth-wasteful for old banners).
 */
const buildSrcSet = (imageUrls) => {
  if (!imageUrls) return '';
  return Object.entries(BANNER_SRCSET_WIDTHS)
    .map(([key, w]) => (imageUrls[key] ? `${resolveImageUrl(imageUrls[key])} ${w}w` : null))
    .filter(Boolean)
    .join(', ');
};

/**
 * Pick the main `src` URL — prefers the `large` variant from the multi-size
 * payload (highest fidelity for browsers that ignore srcset), falls back
 * through the variants, and finally to the legacy single `image_url`.
 */
const pickMainSrc = (banner) => {
  const u = banner.image_urls;
  if (u?.large) return resolveImageUrl(u.large);
  if (u?.medium) return resolveImageUrl(u.medium);
  if (u?.small) return resolveImageUrl(u.small);
  return resolveImageUrl(banner.image_url);
};

/**
 * True when a banner actually carries a displayable image. An image-LESS banner
 * row (admin created it but never uploaded art) would otherwise count toward
 * `banners.length`, defeating the empty-zone null-return and rendering a bare
 * `bg-muted` gray card. Filtered out everywhere banners enter state.
 */
const bannerHasImage = (banner) => {
  if (!banner) return false;
  if (pickMainSrc(banner)) return true;
  const u = banner.image_urls;
  return !!(u && (u.large || u.medium || u.small));
};

export default function BannerSlot({ zone, branchId: branchIdProp, className = '' }) {
  const navigate = useNavigate();
  const seenRef = useRef(new Set());
  const containerRef = useRef(null);

  // Resolve branch from prop first (preferred — Landing passes it from state so
  // changes refetch reliably); fall back to localStorage so pages without a
  // branch switcher (OfferOfTheDay) still work drop-in.
  const branchId = branchIdProp ?? (() => {
    try { return JSON.parse(localStorage.getItem('selectedBranch'))?.id || ''; }
    catch { return ''; }
  })();

  // SWR paint hint: cached banners for this zone/branch paint with the FIRST frame
  // instead of popping in (and shifting Home's layout) a beat after the page loads.
  // `null` = cache miss (slot stays empty until fetch); an array (even empty) = a
  // valid cached state.
  const [banners, setBanners] = useState(() => (readCache(CACHE_KEYS.banners(zone, branchId)) || []).filter(bannerHasImage));
  const [loading, setLoading] = useState(() => readCache(CACHE_KEYS.banners(zone, branchId)) === null);

  // Refetch whenever zone OR branch changes. Earlier this depended only on
  // [zone], which let a branch-scoped banner stay on screen after the user
  // switched branches without a full reload — backend filter was correct but
  // the client never re-asked.
  useEffect(() => {
    let cancelled = false;
    const cached = readCache(CACHE_KEYS.banners(zone, branchId));
    // Paint the new zone/branch's cached banners immediately (covers a branch
    // switch too); only show "loading" (hide the slot) when nothing is cached, so
    // the background refresh never blanks an already-painted banner.
    if (cached !== null) { setBanners(cached.filter(bannerHasImage)); setLoading(false); }
    else { setBanners([]); setLoading(true); }
    seenRef.current = new Set(); // new branch → re-fire impressions
    const params = branchId ? { branch_id: branchId } : {};
    axios.get(`${API}/banners/by-zone/${zone}`, { params })
      .then((res) => {
        if (cancelled) return;
        const list = (Array.isArray(res.data) ? res.data : []).filter(bannerHasImage);
        setBanners(list);
        writeCache(CACHE_KEYS.banners(zone, branchId), list);
      })
      .catch(() => { /* keep whatever we painted from cache */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [zone, branchId]);

  // Banners whose image actually failed to load (404 / broken src). The
  // empty-zone null-return only catches rows with NO url at all; a row that
  // HAS a url string but whose image 404s would still render a blank bg-muted
  // gray card. We hide those on the <img> onError so the zone collapses to
  // nothing when no banner image successfully paints. (Task R8.)
  const [failedIds, setFailedIds] = useState(() => new Set());
  const handleImgError = useCallback((id) => {
    setFailedIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  // One impression per banner per page load.
  const logImpression = useCallback((id) => {
    if (!id || seenRef.current.has(id)) return;
    seenRef.current.add(id);
    axios.post(`${API}/banners/${id}/impression`).catch(() => {});
  }, []);

  const handleClick = useCallback((banner) => {
    const id = banner.id;
    const link = banner.link || {};
    axios.post(`${API}/banners/${id}/click`).catch(() => {});

    followBannerLink(link, navigate);
  }, [navigate]);

  // Autoplay for carousel zones — driven by the Embla API ref. Paused while the
  // user is hovering / touching the carousel root so it doesn't fight gestures.
  const apiRef = useRef(null);
  const pausedRef = useRef(false);
  useEffect(() => {
    if (!CAROUSEL_ZONES.has(zone)) return undefined;
    if (banners.length <= 1) return undefined;
    const interval = setInterval(() => {
      if (pausedRef.current) return;
      const api = apiRef.current;
      if (!api) return;
      if (api.canScrollNext()) api.scrollNext();
      else api.scrollTo(0);
    }, AUTOPLAY_MS);
    return () => clearInterval(interval);
  }, [zone, banners.length]);

  // Drop any banner whose image failed to load so a broken row never reserves
  // a blank gray card; an all-broken zone collapses to nothing.
  const visibleBanners = banners.filter((b) => !failedIds.has(b.id));
  if (loading || visibleBanners.length === 0) return null;

  // Aspect ratio differs per zone (carousels are wide 16:9, strips are 3:1)
  // so the image renders at the correct shape regardless of the source.
  const aspectClass = CAROUSEL_ZONES.has(zone) ? 'aspect-[16/9]' : 'aspect-[3/1]';

  if (CAROUSEL_ZONES.has(zone)) {
    return (
      <div
        ref={containerRef}
        className={`relative w-full ${className}`}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
        onTouchStart={() => { pausedRef.current = true; }}
        onTouchEnd={() => { pausedRef.current = false; }}
        data-testid={`banner-slot-${zone}`}
      >
        <Carousel
          opts={{ loop: true, align: 'start' }}
          setApi={(api) => { apiRef.current = api; }}
          className="w-full"
        >
          <CarouselContent>
            {visibleBanners.map((b) => (
              <CarouselItem key={b.id}>
                <BannerImage
                  banner={b}
                  aspectClass={aspectClass}
                  onClick={() => handleClick(b)}
                  onVisible={() => logImpression(b.id)}
                  onError={() => handleImgError(b.id)}
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    );
  }

  // Single-banner zone
  const b = visibleBanners[0];
  return (
    <div className={`w-full ${className}`} data-testid={`banner-slot-${zone}`}>
      <BannerImage
        banner={b}
        aspectClass={aspectClass}
        onClick={() => handleClick(b)}
        onVisible={() => logImpression(b.id)}
        onError={() => handleImgError(b.id)}
      />
    </div>
  );
}

// Module-level memory of banner image URLs already decoded this session, so a
// re-mount (tab re-visit) paints the cached banner instantly without re-fading
// (same fix as OptimizedImage's loadedUrls).
const bannerLoadedUrls = new Set();

/**
 * Single banner image — handles the IntersectionObserver impression fire and
 * renders an aspect-ratio-locked clickable card. Decorative banners (link.type
 * = "none") render without a pointer cursor.
 */
function BannerImage({ banner, aspectClass, onClick, onVisible, onError }) {
  const imgRef = useRef(null);
  const isInteractive = (banner.link?.type || 'none') !== 'none';
  const mainSrc = pickMainSrc(banner);
  const srcSet = buildSrcSet(banner.image_urls);
  const blurUrl = banner.image_urls?.blur ? resolveImageUrl(banner.image_urls.blur) : '';
  // Already decoded this session → start loaded so we skip the blur + fade.
  const [loaded, setLoaded] = useState(() => !!mainSrc && bannerLoadedUrls.has(mainSrc));
  useEffect(() => {
    const el = imgRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      onVisible?.();
      return undefined;
    }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          onVisible?.();
          obs.disconnect();
        }
      });
    }, { threshold: 0.4 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [onVisible]);

  return (
    <div
      ref={imgRef}
      onClick={isInteractive ? onClick : undefined}
      className={`block relative w-full ${aspectClass} overflow-hidden rounded-xl bg-muted ${isInteractive ? 'cursor-pointer' : ''}`}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : -1}
      onKeyDown={(e) => { if (isInteractive && (e.key === 'Enter' || e.key === ' ')) onClick(); }}
      data-testid={`banner-${banner.id}`}
    >
      {blurUrl && !loaded && (
        <img
          src={blurUrl}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover scale-110"
          style={{ filter: 'blur(16px)' }}
        />
      )}
      <img
        src={mainSrc}
        srcSet={srcSet || undefined}
        sizes={srcSet ? BANNER_SIZES_HINT : undefined}
        alt={banner.image_alt || banner.title || ''}
        loading="lazy"
        decoding="async"
        onLoad={() => { bannerLoadedUrls.add(mainSrc); setLoaded(true); }}
        onError={() => onError?.()}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
      />
    </div>
  );
}
